// ==================== ADMIN SUBSCRIPTIONS SERVICE ====================

import { apiService } from './api';
import type { ApiResponse } from '../types';

// ==================== TYPES ====================

export interface SubscriptionOrganization {
  id: string;
  businessName: string;
  businessEmail: string;
  status: string;
}

export interface SubscriptionPlanInfo {
  id: string;
  name: string;
  monthlyPrice: string;
  yearlyPrice?: string;
}

export interface Subscription {
  id: string;
  status: 'PENDING' | 'ACTIVE' | 'CANCELLED' | 'PAST_DUE' | 'EXPIRED'; 
  billingCycle: 'MONTHLY' | 'YEARLY';
  currentPeriodStart: string;
  currentPeriodEnd: string;
  amount: string;
  coupon?: { code: string; discountPercent: number; discountScope: string; discountDuration: string } | null;
  currency: string;
  stripeSubscriptionId?: string;
  stripeCustomerId?: string;
  cancelledAt?: string;
  createdAt: string;
  updatedAt: string;
  planId: string;
  plan: SubscriptionPlanInfo;
  organizations: SubscriptionOrganization[];
  _count?: {
    payments: number;
  };
}

export interface SubscriptionStatistics {
  total: number;
  active: number;
  cancelled: number;
  pastDue: number;
  monthlyRecurringRevenue: number | null;
  monthlyRecurringRevenueByCurrency: Array<{ currency: string; amount: number }>;
  monthlySubscriptions: number;
  yearlySubscriptions: number;
}

export interface PaginatedSubscriptions {
  data: Subscription[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// ==================== SERVICE ====================

export const subscriptionsService = {
  /**
   * Get all subscriptions with pagination
   */
  getAll: async (options?: {
    page?: number;
    limit?: number;
    status?: string;
    planId?: string;
    search?: string;
  }): Promise<ApiResponse<PaginatedSubscriptions>> => {
    const params = new URLSearchParams();
    if (options?.page) params.append('page', String(options.page));
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.status) params.append('status', options.status);
    if (options?.planId) params.append('planId', options.planId);
    if (options?.search) params.append('search', options.search);
    
    const queryString = params.toString();
    return apiService.get<PaginatedSubscriptions>(
      `/admin/subscriptions${queryString ? `?${queryString}` : ''}`
    );
  },

  /**
   * Get subscription statistics
   */
  getStatistics: async (): Promise<ApiResponse<SubscriptionStatistics>> => {
    return apiService.get<SubscriptionStatistics>('/admin/subscriptions/statistics');
  },

  /**
   * Get subscription by ID
   */
  getById: async (id: string): Promise<ApiResponse<Subscription>> => {
    return apiService.get<Subscription>(`/admin/subscriptions/${id}`);
  },

  /**
   * Update subscription status
   */
  updateStatus: async (
    id: string,
    status: 'ACTIVE' | 'CANCELLED' | 'PAST_DUE' | 'PAUSED'
  ): Promise<ApiResponse<Subscription>> => {
    return apiService.patch<Subscription>(`/admin/subscriptions/${id}/status`, { status });
  },

  /**
   * Change subscription plan
   */
  changePlan: async (
    id: string,
    planId: string,
    billingCycle?: 'MONTHLY' | 'YEARLY'
  ): Promise<ApiResponse<Subscription>> => {
    return apiService.patch<Subscription>(`/admin/subscriptions/${id}/plan`, {
      planId,
      billingCycle,
    });
  },

  /**
   * Get expiring subscriptions
   */
  getExpiring: async (): Promise<ApiResponse<Subscription[]>> => {
    return apiService.get<Subscription[]>('/admin/subscriptions/expiring');
  },
};

export default subscriptionsService;

